import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, ProtectedRoute } from '../components/auth';
import { MainLayout } from '../components/layout';
import { DashboardPage, ContactPage } from '../pages';
import { HomePage } from '../pages/HomePage';
import {
  CandidatesPage,
  CandidateDetailPage,
  CandidateFormPage,
  SemanticSearchPage,
  CVImportPage,
} from '../modules/itc/pages';
import {
  OpportunitySearchPage,
  OpportunitiesPage,
  OpportunityDetailPage,
  DceAnalysisHistoryPage,
  TmCompaniesPage,
  TmCompanyDetailPage,
} from '../modules/tm/pages';
import { CompaniesPage } from '../modules/core/companies/pages/CompaniesPage';
import { UsersPage } from '../modules/core/users/pages/UsersPage';
import { AnalyticsPage } from '../modules/core/analytics/pages/AnalyticsPage';
import { DossierCompanyPage } from '../modules/core/dossier-company';
import { SuggestedOpportunitiesPage } from '../modules/tm/suggested-opportunities/pages/SuggestedOpportunitiesPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});


function UnauthorizedPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-destructive mb-4">403</h1>
        <p className="text-muted-foreground">
          You don't have permission to access this page.
        </p>
        <a href="/" className="mt-4 inline-block text-primary hover:underline">
          Go back home
        </a>
      </div>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Protected routes with MainLayout */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard - Module selection */}
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* ITC Module */}
              <Route path="/itc" element={<Navigate to="/itc/candidates" replace />} />
              <Route path="/itc/candidates" element={<CandidatesPage />} />
              <Route path="/itc/candidates/new" element={<CandidateFormPage />} />
              <Route path="/itc/candidates/import" element={<CVImportPage />} />
              <Route path="/itc/candidates/:id" element={<CandidateDetailPage />} />
              <Route path="/itc/candidates/:id/edit" element={<CandidateFormPage />} />
              <Route path="/itc/search" element={<SemanticSearchPage />} />

              {/* Sprint 5 v3 — Dossier Company (transverse company) */}
              <Route path="/dossier-company" element={<DossierCompanyPage />} />

              {/* TM Module - Tender Management */}
              <Route path="/tm" element={<Navigate to="/tm/search" replace />} />
              <Route path="/tm/search" element={<OpportunitySearchPage />} />
              <Route path="/tm/opportunities" element={<OpportunitiesPage />} />
              {/* Sprint 5b.1.5 v2 — Opportunités suggérées (sourcing automatique) */}
              <Route path="/tm/suggested-opportunities" element={<SuggestedOpportunitiesPage />} />
              <Route path="/tm/opportunities/:id" element={<OpportunityDetailPage />} />
              <Route path="/tm/opportunities/:id/analysis/history/:analysisId" element={<DceAnalysisHistoryPage />} />
              <Route path="/tm/companies" element={<TmCompaniesPage />} />
              <Route path="/tm/companies/:id" element={<TmCompanyDetailPage />} />

              {/* Bridge Module (à implémenter) */}
              <Route path="/bridge/*" element={<ComingSoonPage module="Bridge" />} />
            </Route>

            {/* Admin routes - super_admin only */}
            <Route
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/admin" element={<Navigate to="/admin/companies" replace />} />
              <Route path="/admin/companies" element={<CompaniesPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/analytics" element={<AnalyticsPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  );
}

function ComingSoonPage({ module }: { module: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <h2 className="text-2xl font-bold mb-4">{module}</h2>
      <p className="text-muted-foreground">Ce module est en cours de développement.</p>
    </div>
  );
}
